import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { LoadingButton } from "@mui/lab"
import { Card, CardContent, TextField, Typography } from "@mui/material"
import {
  setCart,
  setCheckout,
  setCheckoutOption,
} from "src/features/checkout/checkoutSlice"
import {
  useLazyGetCheckoutOptionByZipQuery,
  useLazyGetZipCodeDataQuery,
} from "src/features/checkout/checkoutApi"
import { DELIVERY_OPTIONS } from "src/app/constants"
import { useForm } from "react-hook-form"
import TextInput from "../common/TextInput"
import { useRemoveLineItemsMutation } from "src/features/checkout/storefrontApi"

export default function DeliveryOrShippingCard() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { control, handleSubmit, watch, setValue, setError } = useForm({
    defaultValues: {
      zip: "",
    },
  })
  const zip = watch("zip")
  const {customer, cart} = useSelector((state) => state.checkout)

  const [
    getCheckoutOptionByZip,
    {
      data: deliveryOptionByZip,
      isLoading: getCheckoutOptionByZipLoading,
      error: getCheckoutOptionByZipError,
    },
  ] = useLazyGetCheckoutOptionByZipQuery()

  const [getZipData, { data: zipData, isLoading: getZipDataLoading }] =
    useLazyGetZipCodeDataQuery()

  const [removeLineItems] = useRemoveLineItemsMutation()

  function onSubmit(data) {
    const isAsrc = zip === "96898"
    let $zip = isAsrc ? "96701" : zip;

    dispatch(
      setCheckoutOption({
        deliveryOption: null,
        zip: $zip,
        isAsrc,
        zipData: null,
        specialEvent: null,
      }),
    )

    getCheckoutOptionByZip($zip)
    getZipData($zip)
  }

  useEffect(() => {
    // Reset selected options if user navigates back to this page.
    dispatch(
      setCheckoutOption({
        deliveryOption: null,
        zip: null,
        zipData: null,
        specialEvent: null,
        pickupLocation: null,
      }),
    )
  }, [])

  useEffect(() => {
    if (deliveryOptionByZip && zipData) {
      dispatch(
        setCheckoutOption({
          deliveryOption: deliveryOptionByZip,
          zipData,
          specialEvent: null,
        }),
      )

      if (
        [
          DELIVERY_OPTIONS.LOCAL_DELIVERY,
          DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING,
          DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
        ].includes(deliveryOptionByZip)
      ) {
        if (cart.buyerIdentity?.deliveryAddressPreferences?.[0] &&
            cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.zip !== zipData.zip) {
          // Reset address1 and address2 if zip code has been changed.
          dispatch(
            setCart({
              buyerIdentity: {
                email: customer.email || cart.buyerIdentity?.email || "",
                deliveryAddressPreferences: [{
                  ...cart.buyerIdentity.deliveryAddressPreferences[0],
                  address1: "",
                  address2: "",
                }]
              },
            }),
          )
        }
        navigate("../information")
      }
    }
  }, [deliveryOptionByZip, zipData])

  useEffect(() => {
    if (getCheckoutOptionByZipError) {
      if (getCheckoutOptionByZipError.status === 404) {
        setError("zip", {
          type: "custom",
          message: "Please enter a valid ZIP code.",
        })
      } else {
        setError("zip", {
          type: "custom",
          message:
            "Something went wrong while trying to check the ZIP code. Please try again in a while.",
        })
      }
    }
  }, [getCheckoutOptionByZipError])

  return (
    <Card>
      <CardContent>
        <Typography
          align="center"
          variant="overline"
          fontSize={16}
          fontWeight="700"
          lineHeight={1}
        >
          Delivery / Shipping
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            name="zip"
            control={control}
            rules={{
              required: {
                message: "Please enter a ZIP code for Delivery/Shipping.",
                value: true,
              },
              validate: {
                isValid: (value) =>
                  /^\d{5}(?:-\d{4})?$/.test(value.trim()) ||
                  "Please enter a valid ZIP code.",
              },
            }}
            label="Enter ZIP code for Delivery/Shipping"
            fullWidth
            margin="normal"
          />
          <LoadingButton
            type="submit"
            loading={getCheckoutOptionByZipLoading || getZipDataLoading}
            variant="contained"
            size="large"
            fullWidth
            disabled={
              !zip ||
              zip.length < 5 ||
              getCheckoutOptionByZipLoading ||
              getZipDataLoading
            }
          >
            Go
          </LoadingButton>
        </form>
      </CardContent>
    </Card>
  )
}
